import { useEffect, useRef, useCallback } from 'react'
import { useNotification } from '../contexts/NotificationContext'

type AsyncFn<T> = (signal: AbortSignal) => Promise<T>

interface UseApiEffectOptions {
  showErrorNotification?: boolean
  enabled?: boolean
}

/**
 * Hook para executar chamadas de API com AbortController e tratamento de erro centralizado.
 * Elimina a duplicação do pattern useEffect + AbortController encontrado em 24 arquivos.
 *
 * @example
 * // Uso simples
 * const { refetch } = useApiEffect(
 *   async (signal) => {
 *     const res = await api.get('/endpoint', { signal })
 *     setData(res.data)
 *   },
 *   [dependency]
 * )
 *
 * @example
 * // Com condição de execução
 * useApiEffect(
 *   async (signal) => {
 *     const res = await api.get(`/items/${id}`, { signal })
 *     setItem(res.data)
 *   },
 *   [id],
 *   { enabled: !!id }
 * )
 */
export function useApiEffect<T>(
  asyncFn: AsyncFn<T>,
  deps: React.DependencyList,
  options: UseApiEffectOptions = {}
) {
  const { showErrorNotification = true, enabled = true } = options
  const { showApiError } = useNotification()
  const isMountedRef = useRef(true)

  const execute = useCallback(async (signal: AbortSignal) => {
    try {
      await asyncFn(signal)
    } catch (error) {
      if (signal.aborted) return
      if (showErrorNotification) {
        showApiError(error)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    isMountedRef.current = true

    if (!enabled) return

    const controller = new AbortController()
    execute(controller.signal)

    return () => {
      isMountedRef.current = false
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, execute])

  const refetch = useCallback(() => {
    const controller = new AbortController()
    execute(controller.signal)
  }, [execute])

  return { refetch }
}
